import { DEFAULT_LANG, getLang, isRtl, isWrapped, ltr, t, tExact } from "./core";

// The backend writes its action log, ActionResult messages and HTTP `detail`
// errors in French, and the log is shared by every player in the room. Rather
// than making the server per-user aware, each client translates what it shows:
// French sentences are matched here and re-rendered from the `srv.*` templates
// of the active language. Anything unmatched is displayed as-is (French).

const card = (s) => (s === "Joker" ? t("srv.joker") : ltr(s));
const cardList = (s) => s.split(", ").map(card).join(t("srv.listSep"));
const kind = (k) => t(k === "sequence" || k === "suite" ? "srv.kindSeq" : "srv.kindSet");

// "suite A♦-K♦-Q♦… + brelan 7♠-7♥-7♦"
const combos = (s) =>
  s
    .split(" + ")
    .map((part) => {
      const m = /^(suite|brelan) (.+)$/.exec(part);
      return m ? `${kind(m[1])} ${ltr(m[2].replace(/Joker/g, t("srv.joker")))}` : ltr(part);
    })
    .join(" + ");

const posedVars = (m) => ({
  name: m[1],
  combos: combos(m[2]),
  pts: +m[3],
  note: m[4] ? t("srv.thresholdNote", { n: +m[4] }) : "",
});

const N = "(.+?)"; // a player name

// [pattern, key, vars]. Order matters: more specific sentences first.
const PATTERNS = [
  // ── Action log ──────────────────────────────────────────────────────────
  [`^🟢 ${N} s'est reconnecté\\.$`, "srv.reconnected", (m) => ({ name: m[1] })],
  [`^📡 ${N} s'est déconnecté\\.$`, "srv.disconnected", (m) => ({ name: m[1] })],
  [`^Partie démarrée\\. ${N} commence avec 15 cartes\\.$`, "srv.gameStarted", (m) => ({ name: m[1] })],
  [`^Manche (\\d+) — ${N} commence\\.$`, "srv.roundStarted", (m) => ({ n: +m[1], name: m[2] })],
  [`^${N} a pris (\\S+) de la défausse\\.$`, "srv.tookDiscard", (m) => ({ name: m[1], card: card(m[2]) })],
  [
    `^🃏 ${N} a pris la défausse et posé (.+) \\((\\d+) pts(?: posés)?\\)(?: — 🆙 seuil: (\\d+) pts)?\\.$`,
    "srv.tookAndPosed",
    posedVars,
  ],
  [`^🃏 ${N} a posé (.+) \\((\\d+) pts(?: posés)?\\)(?: — 🆙 seuil: (\\d+) pts)?\\.$`, "srv.posed", posedVars],
  [
    `^📤 Brelan de ${N} complet : cassé, ses (\\d+) cartes passent sous la défausse\\.$`,
    "srv.setBroken",
    (m) => ({ name: m[1], n: +m[2] }),
  ],
  [
    `^⚠\uFE0F? ${N} a pris la défausse sans la poser en Nemna3 \\(\\+(\\d+) pts de pénalité\\)\\. Carte rendue\\.$`,
    "srv.tookWithoutPosing",
    (m) => ({ name: m[1], n: +m[2] }),
  ],
  [
    `^⚠\uFE0F? ${N} a défaussé (\\S+) alors que cette carte peut être jouée sur un (\\w+) de ${N}\\. Carte rendue, \\+(\\d+) pts de pénalité\\.$`,
    "srv.usefulDiscard",
    (m) => ({ name: m[1], card: card(m[2]), kind: kind(m[3]), owner: m[4], n: +m[5] }),
  ],
  [`^${N} a défaussé (?:une carte|\\S+)\\.$`, "srv.discarded", (m) => ({ name: m[1] })],
  [
    `^➕ ${N} a ajouté (.+) sur le (suite|brelan) de ${N}\\.$`,
    "srv.added",
    (m) => {
      const counted = /^(\d+) carte\(s\)$/.exec(m[2]);
      return {
        name: m[1],
        cards: counted ? t("srv.nCards", { n: +counted[1] }) : cardList(m[2]),
        kind: kind(m[3]),
        target: m[4],
      };
    },
  ],
  [
    `^♻\uFE0F? ${N} a récupéré un Joker \\((?:groupe|brelan) de ${N}\\) avec (\\S+) \\+ (\\S+)\\.$`,
    "srv.jokerRecovered2",
    (m) => ({ name: m[1], target: m[2], card: card(m[3]), extra: card(m[4]) }),
  ],
  [
    `^♻\uFE0F? ${N} a récupéré un Joker \\((?:groupe|brelan) de ${N}\\) avec (\\S+)\\.$`,
    "srv.jokerRecovered",
    (m) => ({ name: m[1], target: m[2], card: card(m[3]) }),
  ],
  [
    `^🏆 ${N} a gagné la manche avec (\\d+) Joker\\(s\\) en main !$`,
    "srv.nefrechJokers",
    (m) => ({ name: m[1], n: +m[2] }),
  ],
  [`^🏆 ${N} a gagné la manche !$`, "srv.nefrech", (m) => ({ name: m[1] })],
  [
    `^⚠\uFE0F? ${N} a gagné la manche avec la défausse de ${N} → \\+(\\d+) pts pour .+\\.$`,
    "srv.nefrechFausse",
    (m) => ({ name: m[1], victim: m[2], n: +m[3] }),
  ],
  [
    `^⚠\uFE0F? ${N} a tenté un remplacement de Joker invalide \\(\\+(\\d+) pts de pénalité\\)\\.$`,
    "srv.badJokerSwap",
    (m) => ({ name: m[1], n: +m[2] }),
  ],
  [
    `^↩\uFE0F? ${N} a annulé la récupération du Joker \\(groupe de ${N}\\)\\.$`,
    "srv.undoJoker",
    (m) => ({ name: m[1], target: m[2] }),
  ],
  [`^↩\uFE0F? ${N} a annulé l'ajout au groupe de ${N}\\.$`, "srv.undoAdd", (m) => ({ name: m[1], target: m[2] })],
  [
    `^↩\uFE0F? ${N} a annulé toutes les modifications sur le groupe de ${N}\\.$`,
    "srv.undoAll",
    (m) => ({ name: m[1], target: m[2] }),
  ],
  [
    `^❌ ${N} est éliminé\\(e\\) en cours de manche \\(score: (\\d+)\\)\\.$`,
    "srv.eliminatedMidRound",
    (m) => ({ name: m[1], score: +m[2] }),
  ],
  [`^📥 ${N} a rendu (\\d+) carte\\(s\\) à la défausse\\.$`, "srv.returnedCards", (m) => ({ name: m[1], n: +m[2] })],
  [
    `^🏆 ${N} remporte la partie : tous les autres joueurs sont éliminés !$`,
    "srv.wonAllOut",
    (m) => ({ name: m[1] }),
  ],
  [`^🏆 ${N} remporte la partie !$`, "srv.wonGame", (m) => ({ name: m[1] })],
  [`^🏆 ${N} remporte la partie, ${N} termine 2e !$`, "srv.wonGameTwo", (m) => ({ name: m[1], second: m[2] })],
  [
    `^${N} a annulé Nemna3 \\(\\+(\\d+) pts de pénalité\\)\\.$`,
    "srv.cancelledNemna3Penalty",
    (m) => ({ name: m[1], n: +m[2] }),
  ],
  [`^${N} a annulé Nemna3\\.$`, "srv.cancelledNemna3", (m) => ({ name: m[1] })],
  [
    `^${N} a tenté une pose refusée : il manque une suite sans Joker \\(\\+(\\d+) pts de pénalité\\)\\.$`,
    "srv.refusedPoseNoPureRun",
    (m) => ({ name: m[1], n: +m[2] }),
  ],
  [
    `^${N} a tenté une pose refusée : seuil non atteint, (\\d+) pts sur (\\d+) \\(\\+(\\d+) pts de pénalité\\)\\.$`,
    "srv.refusedPoseThreshold",
    (m) => ({ name: m[1], pts: +m[2], threshold: +m[3], n: +m[4] }),
  ],
  [
    `^${N} a annulé Nefrech \\(\\+(\\d+) pts\\)\\.$`,
    "srv.cancelledNefrechPenalty",
    (m) => ({ name: m[1], n: +m[2] }),
  ],
  [`^${N} a annulé Nefrech\\.$`, "srv.cancelledNefrech", (m) => ({ name: m[1] })],
  [`^Cartes redistribuées — ${N} commence avec 15 cartes\\.$`, "srv.redealt", (m) => ({ name: m[1] })],
  [`^${N} a choisi de continuer — la manche reprend\\.$`, "srv.choseContinue", (m) => ({ name: m[1] })],
  // The turn timer (game_service.sync_turn_timer, actions.play_timed_out_turn).
  [`^⏱ Temps écoulé : le Dealer joue pour ${N}\\.$`, "srv.timeUpTurn", (m) => ({ name: m[1] })],
  [`^⏱ Temps écoulé : le Dealer pioche pour ${N}\\.$`, "srv.timeUpDraw", (m) => ({ name: m[1] })],
  [`^⏱ Temps écoulé : ${N} passe son vote\\.$`, "srv.timeUpVote", (m) => ({ name: m[1] })],
  [
    `^${N} veut rejouer \\((\\d+)/(\\d+)\\) — redistribution\\.$`,
    "srv.wantsRedealAll",
    (m) => ({ name: m[1], a: +m[2], b: +m[3] }),
  ],
  [
    `^${N} veut rejouer la manche \\((\\d+)/(\\d+)\\) — au tour de ${N}\\.$`,
    "srv.wantsRedealNext",
    (m) => ({ name: m[1], a: +m[2], b: +m[3], next: m[4] }),
  ],
  [
    `^${N} a choisi de continuer mais n'a posé aucune combinaison → \\+(\\d+) pts\\.$`,
    "srv.continuedNoPose",
    (m) => ({ name: m[1], n: +m[2] }),
  ],
  [`^${N} n'a posé aucune combinaison → \\+(\\d+) pts\\.$`, "srv.noPose", (m) => ({ name: m[1], n: +m[2] })],
  [
    `^${N} termine la manche avec (\\d+) carte\\(s\\) en main dont (\\d+) Joker\\(s\\) → \\+(\\d+) pts\\.$`,
    "srv.endsWithCardsJokers",
    (m) => ({ name: m[1], n: +m[2], jokers: +m[3], pts: +m[4] }),
  ],
  [
    `^${N} termine la manche avec (\\d+) carte\\(s\\) en main → \\+(\\d+) pts\\.$`,
    "srv.endsWithCards",
    (m) => ({ name: m[1], n: +m[2], pts: +m[3] }),
  ],
  [`^${N} est éliminé\\(e\\) \\(score: (\\d+)\\)\\.$`, "srv.eliminated", (m) => ({ name: m[1], score: +m[2] })],

  // ── Errors carrying values ──────────────────────────────────────────────
  [`^Ce n'est pas votre tour \\(c'est le tour de ${N}\\)$`, "srv.notYourTurnOf", (m) => ({ name: m[1] })],
  [`^Impossible de piocher maintenant \\(phase=.*\\)$`, "srv.cantDrawNow"],
  [`^Impossible de défausser maintenant \\(phase=.*\\)$`, "srv.cantDiscardNow"],
  [`^Impossible de poser maintenant \\(phase=.*\\)$`, "srv.cantPoseNow"],
  [`^Pour prendre la Fausse, vous devez être en phase pioche \\(phase actuelle: .*\\)$`, "srv.fausseNeedsDrawPhase"],
  [
    `^La carte prise de la Fausse doit être utilisée dans votre Nemna3\\. \\(\\+(\\d+) pts de pénalité appliqués\\)$`,
    "srv.fausseMustBeUsedPenalty",
    (m) => ({ n: +m[1] }),
  ],
  [
    `^Seuil Nemna3 non atteint : (\\d+) pts posés, il manque (\\d+) pts \\(seuil requis : (\\d+) pts\\)\\. .*$`,
    "srv.thresholdMissing",
    (m) => ({ pts: +m[1], missing: +m[2], threshold: +m[3] }),
  ],
  [
    `^Pose insuffisante: (\\d+) pts, seuil actuel: (\\d+) pts$`,
    "srv.poseTooLow",
    (m) => ({ pts: +m[1], threshold: +m[2] }),
  ],
  [
    `^Cette carte ne peut pas remplacer le Joker dans ce brelan: (.+)$`,
    "srv.cantReplaceInSet",
    (m) => ({ err: translate(m[1]) }),
  ],
  [
    `^Le groupe ne reste pas valide après remplacement : (.+) \\(\\+(\\d+) pts de pénalité\\)$`,
    "srv.invalidAfterSwapPenalty",
    (m) => ({ err: translate(m[1]), n: +m[2] }),
  ],
  [
    `^Le groupe ne reste pas valide après remplacement : (.+)$`,
    "srv.invalidAfterSwap",
    (m) => ({ err: translate(m[1]) }),
  ],
  [`^Impossible d'ajouter à ce brelan: (.+)$`, "srv.cantAddToSet", (m) => ({ err: translate(m[1]) })],
  [
    `^Maximum 14 cartes peuvent être posées par tour, y compris les cartes ajoutées sur des groupes existants \\((\\d+) tentées\\)\\. Le dernier groupe a été annulé\\.$`,
    "srv.max14Group",
    (m) => ({ n: +m[1] }),
  ],
  [
    `^Maximum 14 cartes peuvent être posées par tour, y compris les cartes ajoutées sur des groupes existants \\((\\d+) tentées\\)\\. Le dernier ajout a été annulé\\.$`,
    "srv.max14Add",
    (m) => ({ n: +m[1] }),
  ],
  [
    `^Maximum 14 cartes peuvent être posées par tour \\((\\d+) tentées, y compris les remplacements de Joker\\)\\. Le remplacement a été annulé\\.$`,
    "srv.max14Swap",
    (m) => ({ n: +m[1] }),
  ],
  [
    `^La carte (\\S+) peut être jouée sur un (\\w+) de ${N}\\. Carte rendue, \\+(\\d+) pts de pénalité\\.$`,
    "srv.usefulDiscardErr",
    (m) => ({ card: card(m[1]), kind: kind(m[2]), owner: m[3], n: +m[4] }),
  ],
  [
    `^Cette carte peut être ajoutée à un (\\w+) déjà posé$`,
    "srv.cardFitsCombo",
    (m) => ({ kind: kind(m[1]) }),
  ],
  [
    `^Ce Joker représente un (\\w+) dans la suite — seule cette carte peut le remplacer\\.$`,
    "srv.jokerStandsFor",
    (m) => ({ rank: ltr(m[1]) }),
  ],
  [`^Annulation: \\+(\\d+) pts$`, "srv.cancelPenalty", (m) => ({ n: +m[1] })],
  [
    `^Ce n'est pas votre tour de voter \\(c'est au tour de ${N}\\)$`,
    "srv.notYourVote",
    (m) => ({ name: m[1] }),
  ],
  [`^Vote enregistré — c'est au tour de ${N}$`, "srv.voteSaved", (m) => ({ name: m[1] })],
  [`^Il faut exactement (\\d+) joueurs pour démarrer$`, "srv.needExactPlayers", (m) => ({ n: +m[1] })],
  [`^Nombre de bots doit être entre 0 et (\\d+)$`, "srv.botsRange", (m) => ({ n: +m[1] })],

  // Round summary: "K de hearts" → K♥
  [
    `^(\\S+) de (spades|hearts|diamonds|clubs)$`,
    "srv.penCard",
    (m) => ({ card: ltr(m[1] + { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" }[m[2]]) }),
  ],
].map(([re, key, vars]) => [new RegExp(re, "u"), key, vars]);

function translate(text, seed) {
  const exact = tExact(text);
  if (exact != null) return exact;
  for (const [re, key, vars] of PATTERNS) {
    const m = re.exec(text);
    if (m) return t(key, vars ? vars(m) : undefined, seed);
  }
  return isRtl() ? ltr(text) : text;
}

/**
 * Translate a French backend message for display. `seed` (e.g. the feed
 * message id) keeps the chosen banter variant stable across re-renders.
 */
export function trServer(text, seed) {
  if (!text || typeof text !== "string" || getLang() === DEFAULT_LANG) return text;
  // Client-side feed messages come out of t() already localised (and wrapped).
  // Don't test for Arabic letters here: a French log line can carry an Arabic
  // player name.
  if (isWrapped(text)) return text;
  return translate(text, seed);
}

/** Room names are generated server-side ("Partie de X", "Solo · X"…). */
export function trRoomName(name) {
  if (!name || getLang() === DEFAULT_LANG) return name;
  let m = /^Partie de (.+)$/.exec(name);
  if (m) return t("srv.roomOf", { name: m[1] });
  if (name === "Partie en ligne") return t("srv.roomOnline");
  m = /^Solo · (.+)$/.exec(name);
  if (m) return t("srv.roomSolo", { name: m[1] });
  return name;
}
